import { defineStore } from 'pinia'

import { getUniqId } from '@/utils/getUniqId'
import { scrollToElement } from '@/utils/scrollToElement'

import { StoreId } from '@/store'
import type { AppState } from '@/store/app/app.types'
import {
  SnackbarLocation,
  SnackbarTheme,
  SnackbarType,
} from '@/store/app/app.types'
import { useToggleStore } from '@/store/toggle/toggle'

import { IncomingHttpHeaders } from 'http'

export const useAppStore = defineStore(StoreId.APP, {
  state: (): AppState => ({
    snackbarQueue: [],
    isWebview: false,
    isVisibleApplicationBanner: false,
    activeCategorySlug: '',
    isCategoryBlockScrolling: false,
  }),
  actions: {
    showSnackbar({
      message,
      type = SnackbarType.MESSAGE,
      theme = SnackbarTheme.DARK,
      location = SnackbarLocation.TOP,
      timeout = 3000,
      hyphens = true,
    }: {
      message: string
      type?: SnackbarType
      theme?: SnackbarTheme
      location?: SnackbarLocation
      timeout?: number
      hyphens?: boolean
    }) {
      const uuid = getUniqId()

      const snackbar = {
        uuid,
        message,
        type,
        theme,
        location,
        timeout,
        hyphens,
      }

      const foundIndex = this.snackbarQueue.findIndex((item) => {
        return item.message === message
      })

      // Если snackbar с таким же сообщением, уже есть в очереди, то заменяем по индексу. В ином случае увеличиваем очередь
      if (foundIndex >= 0) {
        this.snackbarQueue[foundIndex] = snackbar
      } else {
        this.snackbarQueue.push(snackbar)
      }

      // Очищаем очередь через заданный таймаут
      setTimeout(() => {
        this.snackbarQueue = this.snackbarQueue.filter((item) => {
          return item.uuid !== uuid
        })
      }, timeout)
    },

    // мутация для определения по заголовкам, является приложение webview или нет
    // в приложении добавляется заголовок `platform` со значением `Android/iOS`
    detectWebview(headers: IncomingHttpHeaders | undefined) {
      if (!headers) {
        this.isWebview = false

        return
      }

      const webviewHeaderName = 'platform'
      const webViewHeaderPossibleValues = new Set(['android', 'ios'])
      const value = headers[webviewHeaderName]

      // если значение заголовока - стринга и входит в список известных нам значений -> это webview
      if (typeof value !== 'string') return
      const lowercase = value
        .split('')
        .map((c) => c.toLowerCase())
        .join('')
      this.isWebview = webViewHeaderPossibleValues.has(lowercase)
    },

    goToCategory(categorySlug: string) {
      const { isDesktop } = useResponsive()
      const { isLandingPageEnabled } = useToggleStore()
      const headerOffset = isDesktop.value ? 130 : 50

      this.activeCategorySlug = categorySlug

      if (categorySlug === 'sales') {
        return navigateTo('/sales/')
      }

      if (!isLandingPageEnabled) {
        return navigateTo(`/category/${categorySlug}/`)
      }

      this.isCategoryBlockScrolling = true

      scrollToElement(categorySlug, headerOffset)

      setTimeout(() => {
        this.isCategoryBlockScrolling = false
      }, 1000)
    },

    setCategory(categorySlug: string) {
      if (this.activeCategorySlug === categorySlug) return

      this.activeCategorySlug = categorySlug
    },

    clearCategory() {
      this.activeCategorySlug = ''
    },
  },
})
